import { Component, ErrorInfo, ReactNode } from "react";
import { Typography, Button, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console and analytics
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Send to analytics if available
    try {
      const analytics = (window as any).analytics;
      if (analytics && analytics.error) {
        analytics.error(error);
      }
    } catch (e) {
      console.error("Failed to send error to analytics:", e);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ onRetry }: { onRetry: () => void }) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.error} gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body1" gutterBottom>
        We're sorry, but something unexpected happened. Please try refreshing
        the page.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={onRetry}
        className={classes.button}
      >
        Refresh Page
      </Button>
    </div>
  );
};
